<script lang="ts" setup>
import type { CSSProperties } from 'vue'
import { computed, onMounted, ref } from 'vue'
import { useWindowSize } from '@vueuse/core'

interface Props {
  text?: string | number
  color?: string
  fontSize?: string
  rotate?: number
  gap?: [number, number]
  visible?: boolean
  fontFamily?: string
}

const props = withDefaults(defineProps<Props>(), {
  text: '水印',
  color: 'rgba(150, 150, 150, 0.2)',
  fontSize: '14px',
  rotate: -40,
  gap: () => [100, 100],
  visible: true,
  fontFamily: 'Arial, sans-serif',
})

const totalCount = ref(20)
const { width, height } = useWindowSize()

const watermarkStyle = computed<CSSProperties>(() => ({
  color: props.color,
  fontSize: props.fontSize,
  fontFamily: props.fontFamily,
  display: 'grid',
  gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(totalCount.value))}, 1fr)`,
  gap: `${props.gap[0]}px ${props.gap[1]}px`,
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 9999,
  opacity: 0.8,
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
}))

function calculateWatermarkCount() {
  const screenWidth = width.value || window.innerWidth || 375
  const screenHeight = height.value || window.innerHeight || 667
  const horizontalCount = Math.ceil(screenWidth / props.gap[0]) + 2
  const verticalCount = Math.ceil(screenHeight / props.gap[1]) + 2
  totalCount.value = horizontalCount * verticalCount
}

onMounted(() => {
  calculateWatermarkCount()
})
</script>

<template>
  <div v-if="visible" class="watermark-container">
    <div class="watermark" :style="watermarkStyle">
      <span
        v-for="i in totalCount"
        :key="i"
        class="watermark-text"
        :style="{ transform: `rotate(${rotate}deg)` }"
      >
        {{ text }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.watermark-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
}

.watermark-text {
  white-space: nowrap;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 2px;
  background: transparent;
}
</style>
